import React, { useContext, useEffect, useState } from 'react'
import { generateHash } from 'common/utils/generateHash'
import { renameFile } from 'common/utils/file/renameFile'
import { useServices } from 'presentation/hooks/use-services'
import { toast } from 'react-toastify'
import downloadFileFromBlob from 'common/utils/downloadFileFromBlob'
import { SurgicalOrderContext } from 'main/factories/pages/doctor/surgical-order/surgical-order-factory'
import { DocumentsList } from 'common/enum/documents-list'
import DocumentAccordion from 'presentation/shared/layouts/DocumentAccordeon'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import SurgicalOrderPendencyLayout from 'presentation/doctor/layouts/SurgicalOrderPendency'
import { useLocation, useHistory } from 'react-router'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import { PendencyTypes } from 'presentation/shared/layouts/PendencyCard'
import { useStores } from 'presentation/hooks/use-stores'
import SheetModalNew from 'presentation/shared/components/SheetModalNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import Divider from 'presentation/shared/components/DividerNew'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { ReactComponent as WriteIcon } from 'presentation/assets/icons/write-icon.svg'
import { ReactComponent as AttachIcon } from 'presentation/assets/icons/attach-icon.svg'
import { IconWrapper } from 'presentation/shared/components/ButtonNew/styles'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { ButtonsWrapper } from 'presentation/patient/components/Forms/ExamSchedulingForm/styles'
import { AuthDivergenceActionEnum } from 'common/enum/auth-divergence-action'

const SurgicalOrderPendency = ({ setIsLoading }: WithLoadingProps) => {
  const history = useHistory()
  const surgicalPendencyService = useServices().surgicalPendency
  const surgicalOrderService = useServices().surgicalOrder
  const { currentAccount } = useStores()
  const currentUser = currentAccount.getCurrentAccount()?.user
  const [documentSuccessModalIsOpen, setDocumentSuccessModalIsOpen] =
    useState(false)
  const [isCancellationRequestOpen, setIsCancellationRequestOpen] =
    useState(false)
  const [isReanalysisMode, setIsReanalysisMode] = useState(false)
  const [reanalysisMode, setReanalysisMode] = useState<
    'write' | 'attach' | null
  >(null)
  const [uploadedReanalysisDocument, setUploadedReanalysisDocument] = useState<
    string | null
  >(null)
  const [expandedReanalysisDocument, setExpandedReanalysisDocument] =
    useState('')
  const {
    state: { currentPendency, pendencies },
    dispatch
  } = useContext<any>(SurgicalOrderContext)
  const { state: location } = useLocation<{
    surgical_pendency_id?: number
    pendency_order_id?: number
    has_new_document?: boolean
    pendency_type?: PendencyTypes
    create_new?: boolean
    surgical_order_id?: number
    doctor_id?: number
    patient_id?: number
  }>()

  const handlePendencyError = () => {
    toast.warning('Não foi possível carregar a pendência, tente novamente.')
    history.push('/pedidos-cirurgicos/pendencias')
  }

  const getPendencyHistory = async (
    surgical_pendency_id: number,
    pendency_type?: PendencyTypes
  ) => {
    setIsLoading(true)

    if (pendency_type === PendencyTypes.HELP_CARD) {
      try {
        const response: any =
          await surgicalPendencyService.loadHelpPendencyHistory(
            surgical_pendency_id
          )

        dispatch({
          type: 'CURRENT_SURGICAL_PENDENCY',
          payload: response.data
        })
        return response.data
      } catch {
        toast.error('Ocorreu um erro ao carregar a pendência')
      } finally {
        setIsLoading(false)
      }
    } else {
      try {
        const response: any =
          await surgicalPendencyService.loadSurgicalOrderPendenciesHistory(
            surgical_pendency_id
          )

        dispatch({
          type: 'CURRENT_SURGICAL_PENDENCY',
          payload: response.data
        })
        return response.data
      } catch {
        toast.error('Ocorreu um erro ao carregar a pendência')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const uploadDocument = async (
    files: File[],
    type: string,
    groupId?: string
  ) => {
    if (!currentPendency.surgical_pendency_id) return handlePendencyError()
    setIsLoading(true)
    const filesList = [...files]

    await Promise.all(
      filesList.map(async (file) => {
        try {
          const form = new FormData()
          const newFile = renameFile(file, file.name)
          form.append('file', newFile)
          form.append('type', type)
          form.append('group_id', groupId ?? generateHash())
          form.append(
            'surgical_pendency_id',
            String(currentPendency.surgical_pendency_id)
          )

          await surgicalPendencyService.uploadSurgicalPendencyDocument({
            type: newFile.type,
            form: form,
            file: newFile
          })
        } catch {
          toast.error('Ocorreu um erro ao enviar o documento')
        }
      })
    )
      .then(() => getPendencyHistory(currentPendency.surgical_pendency_id))
      .finally(() => setIsLoading(false))
  }

  const deleteDocument = async (document_id: number, group_id: string) => {
    if (!currentPendency.surgical_pendency_id) return handlePendencyError()
    try {
      setIsLoading(true)
      await surgicalPendencyService.removeDocumentFromSurgicalPendency({
        surgical_pendency_id: currentPendency.surgical_pendency_id,
        document_id,
        group_id
      })
      await getPendencyHistory(currentPendency.surgical_pendency_id)
    } catch {
      toast.error('Ocorreu um erro ao excluir o documento')
    } finally {
      setIsLoading(false)
    }
  }

  const downloadDocument = async (
    document_id: number,
    group_id: string,
    type: keyof typeof DocumentsList,
    fileName?: string
  ) => {
    if (!currentPendency.surgical_pendency_id) return handlePendencyError()
    try {
      setIsLoading(true)
      const document =
        await surgicalPendencyService.loadSurgicalPendencyDocument({
          surgical_pendency_id: currentPendency.surgical_pendency_id,
          document_id,
          group_id
        })

      if (document) {
        const originalExtension = fileName?.split('.').pop()?.toLowerCase()
        const extension = originalExtension || ''
        const downloadName = extension
          ? `${DocumentsList[type]}.${extension}`
          : DocumentsList[type]

        downloadFileFromBlob(document.data, document.contentType, downloadName)
      }
    } catch {
      toast.error('Ocorreu um erro ao baixar o documento')
    } finally {
      setIsLoading(false)
    }
  }

  const goToOrderDetails = () => {
    history.push('/pedido-cirurgico/detalhes', {
      surgical_order_id: currentPendency.surgical_order_id
    })
  }

  const answerSurgicalPendency = async (
    values: {
      answer: string
      divergence_resolution_action?: AuthDivergenceActionEnum
      reanalysis_text?: string
    },
    groupId?: string
  ) => {
    try {
      setIsLoading(true)

      if (currentPendency.type === PendencyTypes.HELP_CARD) {
        await surgicalPendencyService.createHelpPendencyMessage({
          surgical_pendency_id: currentPendency.surgical_pendency_id,
          message: values.answer
        })
      } else if (currentPendency.type === PendencyTypes.DOCTOR_COMMUNICATION) {
        const isFirstMessage =
          !currentPendency.surgical_pendency_id ||
          currentPendency.historic.length === 0

        if (isFirstMessage) {
          const created: any =
            await surgicalPendencyService.createDoctorCommunication({
              surgical_order_id: currentPendency.surgical_order_id,
              message: values.answer
            })

          history.replace('/pedido-cirurgico/pendencia', {
            surgical_pendency_id: created.data.surgical_pendency_id,
            pendency_type: PendencyTypes.DOCTOR_COMMUNICATION
          })

          await getPendencyHistory(
            created.data.surgical_pendency_id,
            PendencyTypes.DOCTOR_COMMUNICATION
          )
        } else {
          await surgicalPendencyService.answerDoctorCommunication({
            surgical_pendency_id: currentPendency.surgical_pendency_id,
            message: values.answer
          })
        }
      } else {
        const payload: any = {
          answer: values.answer,
          surgical_pendency_id: currentPendency.surgical_pendency_id,
          divergence_resolution_action: values.divergence_resolution_action
        }
        if (values.reanalysis_text) {
          payload.reanalysis_text = values.reanalysis_text
        } else {
          payload.group_id = groupId ?? generateHash()
        }
        await surgicalPendencyService.answerSurgicalPendency(payload)
      }

      goToOrderDetails()
    } catch (error) {
      toast.error('Ocorreu um erro ao responder a pendencia')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcceptDivergence = async () => {
    try {
      const doctorName = currentUser?.name || 'Médico'
      const message = `Dr(a). ${doctorName} aceitou a divergência no pedido cirúrgico.`

      await answerSurgicalPendency({
        answer: message,
        divergence_resolution_action: AuthDivergenceActionEnum.ACCEPTED
      })
      toast.success('Divergência aceita com sucesso')
    } catch (error) {
      toast.error('Ocorreu um erro ao aceitar a divergência')
    }
  }

  const handleRequestReanalysis = () => {
    setIsReanalysisMode(true)
  }

  const handleCancelSurgicalOrder = () => {
    setIsCancellationRequestOpen(true)
  }

  const reloadPendencyHistory = async () => {
    return await getPendencyHistory(
      currentPendency.surgical_pendency_id,
      PendencyTypes.DOCTOR_COMMUNICATION
    )
  }

  const handleReanalysisDocumentUpload = async (files: any) => {
    if (files && files.length > 0) {
      const file = files[0] as File
      const groupId = generateHash()
      setUploadedReanalysisDocument(groupId)
      await uploadDocument([file], 'Relatório Médico', groupId)
      toast.success('Relatório anexado com sucesso')
    }
  }

  const changeExpandedReanalysisAccordeon = (
    accordeonLabel: string,
    isExpanded: boolean
  ) => {
    setExpandedReanalysisDocument(isExpanded ? accordeonLabel : '')
  }

  const cancellationFormik = useFormik({
    initialValues: {
      cancellation_reason: ''
    },
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: yup.object().shape({
      cancellation_reason: yup
        .string()
        .required()
        .max(200, 'Máximo 200 caracteres.')
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true)
        await surgicalOrderService.requestSurgicalOrderCancellation({
          surgical_order_id: currentPendency.surgical_order_id,
          reason: values.cancellation_reason
        })
        toast.success('Solicitação de cancelamento enviada com sucesso')
        goToOrderDetails()
      } catch (error) {
        toast.error(
          'Ocorreu um erro ao solicitar cancelamento do pedido cirúrgico'
        )
      } finally {
        setIsLoading(false)
        setIsCancellationRequestOpen(false)
      }
    }
  })

  const reanalysisFormik = useFormik({
    initialValues: {
      justification: ''
    },
    validateOnChange: true,
    validateOnMount: true,
    validationSchema: yup.object().shape({
      justification: yup.string().when([], {
        is: () => reanalysisMode === 'write',
        then: (schema) => schema.required('Campo obrigatório')
      })
    }),
    onSubmit: async () => {
      try {
        setIsLoading(true)
        const doctorName = currentUser?.name || 'Médico'
        if (reanalysisMode === 'write') {
          const message = `Dr(a). ${doctorName} solicitou reanálise do pedido e aguarda o seu retorno: ${reanalysisFormik.values.justification}`
          await answerSurgicalPendency({
            answer: message,
            divergence_resolution_action:
              AuthDivergenceActionEnum.REQUEST_REANALYSIS,
            reanalysis_text: reanalysisFormik.values.justification
          })
          toast.success('Solicitação de reanálise enviada com sucesso')
        } else if (reanalysisMode === 'attach') {
          if (!uploadedReanalysisDocument) {
            toast.error('Selecione um relatório para anexar')
            return
          }

          const message = `Dr(a). ${doctorName} solicitou a reanálise do pedido e aguarda o seu retorno. Relatório médico anexado.`
          await answerSurgicalPendency(
            {
              answer: message,
              divergence_resolution_action:
                AuthDivergenceActionEnum.REQUEST_REANALYSIS
            },
            uploadedReanalysisDocument
          )
          toast.success('Solicitação de reanálise enviada com sucesso')
        }
        await getPendencyHistory(currentPendency.surgical_pendency_id)
        history.push('/pedido-cirurgico/pendencia', {
          surgical_pendency_id: currentPendency.surgical_pendency_id
        })
      } catch (error) {
        toast.error('Ocorreu um erro ao solicitar reanálise')
      } finally {
        setIsLoading(false)
        setIsReanalysisMode(false)
        setReanalysisMode(null)
        setUploadedReanalysisDocument(null)
        setExpandedReanalysisDocument('')
        reanalysisFormik.resetForm()
      }
    }
  })

  useEffect(() => {
    if (location.surgical_pendency_id) {
      if (location.pendency_type === PendencyTypes.DOCTOR_COMMUNICATION) {
        const allPendencies = [
          ...(pendencies.in_progress || []),
          ...(pendencies.pendent || []),
          ...(pendencies.resolved || [])
        ]

        const foundPendency = allPendencies.find(
          (p: any) => p.surgical_pendency_id === location.surgical_pendency_id
        )

        if (foundPendency) {
          dispatch({
            type: 'CURRENT_SURGICAL_PENDENCY',
            payload: foundPendency
          })
          getPendencyHistory(
            location.surgical_pendency_id,
            PendencyTypes.DOCTOR_COMMUNICATION
          )
        } else {
          handlePendencyError()
        }
      } else {
        getPendencyHistory(
          location.surgical_pendency_id,
          location.pendency_type
        )
      }
      if (location.has_new_document) setDocumentSuccessModalIsOpen(true)
    } else if (location.create_new && location.surgical_order_id) {
      dispatch({
        type: 'CURRENT_SURGICAL_PENDENCY',
        payload: {
          surgical_pendency_id: 0,
          surgical_order_id: location.surgical_order_id,
          type: location.pendency_type,
          status: 'PENDENT',
          historic: [],
          documents: []
        }
      })
    }
  }, [])

  return (
    <>
      <HeaderNew
        actualPageTitle="Pedido cirúrgico"
        actualPageOnClick={() => {
          if (isReanalysisMode) {
            history.push('/pedido-cirurgico/pendencia', {
              surgical_pendency_id: currentPendency.surgical_pendency_id
            })
            setIsReanalysisMode(false)
            setReanalysisMode(null)
            setUploadedReanalysisDocument(null)
            setExpandedReanalysisDocument('')
          } else {
            goToOrderDetails()
          }
        }}
      />
      {isReanalysisMode ? (
        <ContainerNew gap="16px">
          <SupportTextNew weight="bold" size="medium" color="neutral900">
            Solicitar reanálise
          </SupportTextNew>
          <SupportTextNew size="small" color="neutral900">
            Clique em <b>Anexar relatório</b> para incluir documento ou preencha
            justificativa para gerar um relatório automaticamente.
          </SupportTextNew>
          <ButtonsWrapper style={{ alignItems: 'flex-start' }}>
            <ButtonNew
              size="medium"
              variant="text"
              onClick={() => setReanalysisMode('write')}
              icon={
                <IconWrapper
                  iconColor={reanalysisMode === 'write' ? '#038E83' : '#2D2F34'}
                >
                  <WriteIcon />
                </IconWrapper>
              }
              iconPosition="left"
              style={{
                backgroundColor:
                  reanalysisMode === 'write' ? '#e0f1f0' : 'transparent',
                color: reanalysisMode === 'write' ? '#038E83' : '#2D2F34'
              }}
            >
              Escrever relatório
            </ButtonNew>
            <ButtonNew
              size="medium"
              variant="text"
              onClick={() => {
                setReanalysisMode('attach')
                setExpandedReanalysisDocument('Relatório Médico')
              }}
              icon={
                <IconWrapper
                  iconColor={
                    reanalysisMode === 'attach' ? '#038E83' : '#2D2F34'
                  }
                >
                  <AttachIcon />
                </IconWrapper>
              }
              iconPosition="left"
              style={{
                backgroundColor:
                  reanalysisMode === 'attach' ? '#e0f1f0' : 'transparent',
                color: reanalysisMode === 'attach' ? '#038E83' : '#2D2F34'
              }}
            >
              Anexar relatório
            </ButtonNew>
          </ButtonsWrapper>
          {reanalysisMode === 'write' && (
            <>
              <SupportTextNew weight="semiBold" size="small" color="neutral900">
                Preencher justificativa
              </SupportTextNew>
              <TextAreaNew
                placeholder="Escreva a justificativa para gerar o relatório com o seu pedido de reanálise"
                rows={6}
                id="justification"
                name="justification"
                value={reanalysisFormik.values.justification}
                onChange={reanalysisFormik.handleChange}
                error={reanalysisFormik.errors.justification}
              />
            </>
          )}
          {reanalysisMode === 'attach' && (
            <>
              <SupportTextNew weight="bold" size="medium" color="neutral900">
                Anexar relatório
              </SupportTextNew>
              <SupportTextNew size="small" color="neutral900">
                Inclua relatório em um documento assinado e carimbado
              </SupportTextNew>
              <DocumentAccordion
                document={{
                  type: 'Relatório Médico',
                  key: 'relatorio-medico-reanalise',
                  status: uploadedReanalysisDocument
                    ? 'NOT_UPLOADED'
                    : 'not uploaded',
                  documents: uploadedReanalysisDocument
                    ? (
                        currentPendency.documents?.find(
                          (docGroup: any) =>
                            docGroup.type === 'Relatório Médico'
                        )?.documents || []
                      ).filter(
                        (doc: any) =>
                          doc.group_id === uploadedReanalysisDocument
                      )
                    : []
                }}
                padding="0"
                hideHeaderDivider
                handleChange={changeExpandedReanalysisAccordeon}
                expandedDocument={expandedReanalysisDocument}
                onDeleteFile={() => {
                  setUploadedReanalysisDocument(null)
                }}
                onUploadFile={handleReanalysisDocumentUpload}
                multiple={false}
                uploadButton={true}
              />
            </>
          )}
          <ButtonNew
            fullWidth
            size="large"
            disabled={
              reanalysisMode === 'write'
                ? !!reanalysisFormik.errors.justification ||
                  !reanalysisFormik.values.justification
                : reanalysisMode === 'attach'
                ? !uploadedReanalysisDocument
                : true
            }
            onClick={reanalysisFormik.submitForm}
          >
            Solicitar reanálise
          </ButtonNew>
        </ContainerNew>
      ) : (
        <SurgicalOrderPendencyLayout
          pendencyOrderId={location.pendency_order_id}
          uploadDocument={uploadDocument}
          deleteDocument={deleteDocument}
          downloadDocument={downloadDocument}
          answerSurgicalPendency={answerSurgicalPendency}
          documentSuccessModalIsOpen={documentSuccessModalIsOpen}
          setDocumentSuccessModalIsOpen={setDocumentSuccessModalIsOpen}
          onAcceptDivergence={handleAcceptDivergence}
          onRequestReanalysis={handleRequestReanalysis}
          onCancelSurgicalOrder={handleCancelSurgicalOrder}
          reloadPendencyHistory={reloadPendencyHistory}
        />
      )}
      <SheetModalNew
        isOpen={isCancellationRequestOpen}
        onClose={() => setIsCancellationRequestOpen(false)}
        title="Cancelar pedido cirúrgico"
      >
        <SupportTextNew
          weight="semiBold"
          size="small"
          color="neutral900"
          style={{ marginBottom: '24px' }}
        >
          Tem certeza que deseja cancelar este pedido cirúrgico?
        </SupportTextNew>
        <SupportTextNew size="small" color="neutral900">
          Esta <b>solicitação</b> será recebida pela CRMO que cuidará do
          processo de cancelamento do seu pedido.
        </SupportTextNew>
        <Divider />
        <TextAreaNew
          label="Qual o motivo do cancelamento?"
          required
          requiredColor="neutral600"
          placeholder="Descreva o motivo aqui."
          style={{ marginBottom: '24px' }}
          id="cancellation_reason"
          name="cancellation_reason"
          value={cancellationFormik.values.cancellation_reason}
          onChange={cancellationFormik.handleChange}
          error={cancellationFormik.errors.cancellation_reason}
        />
        <ButtonNew
          fullWidth
          size="large"
          disabled={!!cancellationFormik.errors.cancellation_reason}
          onClick={cancellationFormik.submitForm}
        >
          Cancelar pedido cirúrgico
        </ButtonNew>
      </SheetModalNew>
    </>
  )
}

export default WithLoading(SurgicalOrderPendency)
